import Slider from '@ui/components/Slider'
import Input from '@ui/forms/Input'
import { useWindowDimensions, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { Measurement, Side, ResultValue } from '../types'
import { formatOffsetMm } from '../format'
import { useState } from 'react'
import ManualSidesSwitch from './ManualSidesSwitch'

export const OFFSET_MIN_MM = -5
export const OFFSET_MAX_MM = 5
export const OFFSET_STEP_MM = 0.1

interface Props {
  measurement: Measurement
  result: ResultValue
  side: Side
  offset: number
  disabled?: boolean
  onSideChange: (side: Side) => void
  onOffsetChange: (offset: number) => void
}

const clampOffset = (value: number) =>
  Math.min(Math.max(value, OFFSET_MIN_MM), OFFSET_MAX_MM)

export default function ManualOffset({
  measurement,
  result,
  side,
  offset,
  disabled,
  onSideChange,
  onOffsetChange,
}: Props) {
  const { width, height } = useWindowDimensions()
  const isLandscape = width > height

  const { t } = useTranslation(['measurement', 'ui'])
  const tNamespace = `measurement:app.manual`
  const tl = `${tNamespace}.${measurement}`
  // Held only while the field is focused, so partial entries ("-", "1.") are
  // not overwritten by the round trip through the numeric prop.
  const [draft, setDraft] = useState<string | null>(null)

  const handleOffsetText = (text: string) => {
    setDraft(text)
    const parsed = Number(text.trim().replace(',', '.'))
    if (text.trim() === '' || !Number.isFinite(parsed)) return
    onOffsetChange(clampOffset(parsed))
  }

  return (
    <View className="flex w-full flex-col items-center justify-start gap-8 rounded-2xl border border-black/10 p-8">
      {!isLandscape && (
        <ManualSidesSwitch
          className="w-full"
          measurement={measurement}
          side={side}
          onChange={onSideChange}
        />
      )}
      <View className="flex w-full flex-row items-center justify-center rounded-xl border border-black/10 px-2 py-0">
        <Slider
          value={offset}
          min={OFFSET_MIN_MM}
          max={OFFSET_MAX_MM}
          step={OFFSET_STEP_MM}
          buttonStart={formatOffsetMm(OFFSET_MIN_MM)}
          buttonEnd={formatOffsetMm(OFFSET_MAX_MM)}
          showPercentageLabel={false}
          disabled={disabled}
          onChange={(value) => onOffsetChange(clampOffset(value))}
        />
      </View>
      <View className="flex flex-1 flex-row gap-8">
        <View className="flex flex-1">
          <Input
            label={t('ui:offset')}
            value={draft ?? offset.toFixed(1)}
            keyboardType="numbers-and-punctuation"
            disabled={disabled}
            onChangeText={handleOffsetText}
            onBlur={() => setDraft(null)}
          />
        </View>
        <View className="flex flex-1">
          <Input
            disabled
            label={t('ui:result')}
            value={result.value.toFixed(2)}
          />
        </View>
      </View>
    </View>
  )
}
